import onboardingGuides from '~/onboarding';
import type { UserType } from './types';
import { fetchDoneGuides, updateDoneGuide } from '~/services/onboarding';

type StoredState = {
  lastGuide?: string;
  lastStep?: number | null;
  postponedUntil?: string;
};

type InitOptions = {
  userType: UserType;
  userId: string;
};

export default function useOnboarding() {
  const onboardingStore = useOnboardingStore();
  const { nextStep, previousStep, getCurrentState, postpone, finish, addGuide, open } = onboardingStore;
  const { currentGuide, currentStep, isWelcomeStep, isLastStep, stepIndex, stepCount, user, readTime, openType } =
    storeToRefs(onboardingStore);

  async function init(options: InitOptions) {
    const { userId, userType } = options;
    const onboardingState = JSON.parse(localStorage.getItem('onboarding') ?? '{}');
    const guidesDone: string[] = (await fetchDoneGuides(userType, userId))?.seenTutorialsList ?? [];

    onboardingStore.init({
      guides: onboardingGuides.filter((guide) => !guidesDone.includes(guide.id)),
      lastGuide: onboardingState.lastGuide ?? null,
      lastStep: onboardingState.lastStep ?? null,
      postponedUntil: onboardingState.postponedUntil ? new Date(onboardingState.postponedUntil) : undefined,
      user: {
        userType,
        userId,
      },
    });
  }

  function saveState(state: CurrentGuideState | null = getCurrentState()) {
    if (!state) {
      return;
    }

    const storedState: StoredState = {
      lastGuide: state.guideId,
      lastStep: state.stepIndex,
    };

    if (state.postponedUntil) {
      storedState.postponedUntil = state.postponedUntil.toISOString();
    }

    window.localStorage.setItem('onboarding', JSON.stringify(storedState));
  }

  function postponeGuide() {
    const state = postpone();
    saveState(state);
  }

  function next() {
    nextStep();
    saveState();
  }

  function previous() {
    previousStep();
    saveState();
  }

  async function finishGuide() {
    const guideId = currentGuide.value;
    finish();
    window.localStorage.removeItem('onboarding');

    if (user.value) {
      await updateDoneGuide({
        userId: user.value.userId,
        userType: user.value.userType,
        tutorialId: guideId,
      });
    }
  }

  function showGuide(guideId: string, triggerSource: 'manual' | 'auto' = 'auto') {
    openType.value = triggerSource;
    const guide = onboardingGuides.find((guide) => guide.id === guideId);
    if (guide) {
      addGuide(guide);
      open(guide.id, true);
    }
  }

  return {
    currentStep,
    isWelcomeStep,
    isLastStep,
    readTime,
    stepIndex,
    stepCount,
    openType,
    postponeGuide,
    next,
    previous,
    finishGuide,
    init,
    showGuide,
  };
}
